<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-card elevation="6" rounded="xxl" :loading="loading">
            <v-card-title class="headline primary--text">
              <v-icon class="mr-2">mdi-trophy</v-icon>
              Edit Award
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                small
                outlined
                @click="confirmDelete"
                :disabled="saving"
              >
                <v-icon left small>mdi-delete</v-icon>
                Delete
              </v-btn>
            </v-card-title>

            <v-card-text v-if="!loading">
              <!-- Employee Info Display -->
              <v-row v-if="award && award.user" class="mb-4">
                <v-col cols="12">
                  <v-card outlined class="pa-3">
                    <div class="d-flex align-center">
                      <v-avatar size="48" class="mr-3">
                        <v-img
                          v-if="award.user.image_path"
                          :src="getImageUrl(award.user.image_path)"
                          :alt="award.user.first_name"
                        ></v-img>
                        <v-icon v-else color="grey lighten-1" size="24">mdi-account</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-h6 font-weight-medium">
                          {{ getUserName(award.user) }}
                        </div>
                        <div class="text-body-2 grey--text">
                          {{ award.user.email }}
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <!-- Employee Selection (Read-only in edit mode) -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.user_id"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      label="Employee"
                      :rules="[rules.required]"
                      solo
                      rounded
                      dense
                      required
                      :loading="loadingUsers"
                      :disabled="true"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-account</v-icon>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Award Name -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.name"
                      label="Award Name"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-trophy</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Year -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.year"
                      :items="yearOptions"
                      item-text="text"
                      item-value="value"
                      label="Year"
                      solo
                      rounded
                      dense
                      :rules="[rules.validYear]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-calendar</v-icon>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Current Image Display -->
                  <v-col cols="12" md="6" v-if="award && award.image_path">
                    <div class="text-subtitle-2 mb-2">Current Image</div>
                    <v-card outlined class="pa-3">
                      <v-img
                        :src="getImageUrl(award.image_path)"
                        max-width="200"
                        max-height="200"
                        contain
                        class="mx-auto"
                      ></v-img>
                      <v-btn
                        small
                        text
                        color="error"
                        @click="removeCurrentImage"
                        class="mt-2"
                      >
                        <v-icon left small>mdi-delete</v-icon>
                        Remove Current Image
                      </v-btn>
                    </v-card>
                  </v-col>

                  <!-- Image Upload -->
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="form.image"
                      label="Award Certificate/Image"
                      solo
                      rounded
                      dense
                      accept="image/*"
                      prepend-icon=""
                      :rules="[rules.imageSize]"
                      @change="handleImageUpload"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-image</v-icon>
                      </template>
                    </v-file-input>
                  </v-col>

                  <!-- New Image Preview -->
                  <v-col cols="12" v-if="imagePreview">
                    <v-card outlined class="pa-3">
                      <div class="text-subtitle-2 mb-2">New Image Preview</div>
                      <v-img
                        :src="imagePreview"
                        max-width="200"
                        max-height="200"
                        contain
                        class="mx-auto"
                      ></v-img>
                      <v-btn
                        small
                        text
                        color="error"
                        @click="removeNewImage"
                        class="mt-2"
                      >
                        <v-icon left small>mdi-delete</v-icon>
                        Remove New Image
                      </v-btn>
                    </v-card>
                  </v-col>

                  <!-- Status -->
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="form.is_active"
                      label="Active Status"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>

                  <!-- Metadata -->
                  <v-col cols="12" v-if="award">
                    <v-card outlined class="pa-3">
                      <v-row>
                        <v-col cols="12" md="6">
                          <div class="text-caption grey--text">Created At</div>
                          <div class="text-body-2">{{ formatDateTime(award.created_at) }}</div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <div class="text-caption grey--text">Updated At</div>
                          <div class="text-body-2">{{ formatDateTime(award.updated_at) }}</div>
                        </v-col>
                        <v-col cols="12" v-if="award.created_by">
                          <div class="text-caption grey--text">Created By</div>
                          <div class="text-body-2">{{ getUserName(award.created_by) }}</div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-6" v-if="!loading">
              <v-btn
                color="grey"
                text
                @click="goBack"
                :disabled="saving"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="updateAward"
                :loading="saving"
                :disabled="!valid"
              >
                <v-icon left>mdi-content-save</v-icon>
                Update Award
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this award?
          <br><strong>{{ form.name || 'This award' }}</strong>
          <br><br>This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteAward" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "AwardEdit",
  data() {
    return {
      loading: true,
      valid: false,
      saving: false,
      deleting: false,
      loadingUsers: false,
      deleteDialog: false,
      users: [],
      award: null,
      imagePreview: null,
      
      // Form data
      form: {
        user_id: null,
        name: "",
        year: null,
        image: null,
        image_path: "",
        is_active: true,
      },
      
      // Year options
      yearOptions: [],
      
      // Validation rules
      rules: {
        required: value => !!value || "This field is required",
        maxLength: max => value => !value || value.length <= max || `Maximum ${max} characters allowed`,
        validYear: value => {
          if (!value) return true;
          const currentYear = new Date().getFullYear();
          return (value >= 1900 && value <= currentYear + 5) || "Please enter a valid year";
        },
        imageSize: value => {
          if (!value) return true;
          return value.size <= 5000000 || "Image size should be less than 5MB";
        },
      },
    };
  },

  mounted() {
    this.getUsers();
    this.generateYearOptions();
    this.getAward();
  },

  methods: {
    async getAward() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        const response = await axios.get(`/awards/${id}`);
        
        this.award = response.data.data;
        
        // Populate form
        this.form = {
          user_id: this.award.user_id,
          name: this.award.name || "",
          year: this.award.year,
          image: null,
          image_path: this.award.image_path || "",
          is_active: this.award.is_active,
        };
      } catch (error) {
        console.error("Error fetching award:", error);
        if (error.response && error.response.status === 404) {
          this.$toast.error("Award not found");
          this.$router.push("/awards");
        } else {
          this.$toast.error("Failed to load award");
        }
      } finally {
        this.loading = false;
      }
    },

    async getUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: 1000 }
        });
        
        this.users = response.data.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 10;
      const endYear = currentYear + 5;
      
      this.yearOptions = [];
      for (let year = endYear; year >= startYear; year--) {
        this.yearOptions.push({
          text: year.toString(),
          value: year
        });
      }
    },

    handleImageUpload(file) {
      if (file) {
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },

    removeCurrentImage() {
      this.form.image_path = "";
    },

    removeNewImage() {
      this.form.image = null;
      this.imagePreview = null;
    },

    async updateAward() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fix validation errors");
        return;
      }

      this.saving = true;
      try {
        const id = this.$route.params.id;
        
        // Prepare FormData for file upload
        const formData = new FormData();
        
        // Add all form fields
        Object.keys(this.form).forEach((key) => {
          if (key === 'image') {
            // Handle the file separately
            if (this.form.image) {
              formData.append('image_path', this.form.image);
            }
          } else {
            // Add other form data, convert empty strings to null
            const value = this.form[key] === "" ? null : this.form[key];
            if (value !== null) {
              formData.append(key, value);
            }
          }
        });

        await axios.patch(`/awards/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        
        this.$toast.success("Award updated successfully");
        this.getAward(); // Refresh data
      } catch (error) {
        console.error("Error updating award:", error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          // Display validation errors
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            this.$toast.error(errors[key][0]);
          });
        } else {
          this.$toast.error("Failed to update award");
        }
      } finally {
        this.saving = false;
      }
    },

    confirmDelete() {
      this.deleteDialog = true;
    },

    async deleteAward() {
      this.deleting = true;
      try {
        const id = this.$route.params.id;
        await axios.post(`/awards/delete/${id}`);
        
        this.$toast.success("Award deleted successfully");
        this.$router.push("/awards");
      } catch (error) {
        console.error("Error deleting award:", error);
        this.$toast.error("Failed to delete award");
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
      }
    },

    goBack() {
      this.$router.push("/awards");
    },

    getUserName(user) {
      if (!user) return 'N/A';
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'N/A';
    },

    getImageUrl(imagePath) {
      return this.mediaUrl + '/' + imagePath;
    },

    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      return new Date(dateTime).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 20px !important;
}

.v-text-field--outlined >>> fieldset {
  border-radius: 25px;
}

.v-file-input >>> .v-input__control .v-input__slot {
  border-radius: 25px;
}
</style>